import { Injectable } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { OrganizationService } from "app/core/auth/organization.service";
import { AppConfigService } from "app/services/app-config.service";

/** Minimum CPM (€) for the APPCELERATE LATAM organization. */
export const LATAM_CPM_MINIMUM = 0.1;

/** Minimum CPM (€) for every other organization. */
export const DEFAULT_CPM_MINIMUM = 0.25;

/** Minimum CPC (€). Currently the same for every organization. */
export const DEFAULT_CPC_MINIMUM = 0.001;

/** Organization names (case-insensitive) that use the reduced CPM minimum. */
const LATAM_ORGANIZATION_NAMES = ["APPCELERATE LATAM"];

/**
 * Resolves the minimum CPM/CPC bids allowed for the organization the campaign or line item
 * is being created under. MediaSmart applies a lower CPM floor for APPCELERATE LATAM than
 * for the rest of the organizations; the minimum does not depend on the targeted country.
 */
@Injectable({ providedIn: "root" })
export class BidMinimumsService {
    constructor(
        private _organization: OrganizationService,
        private _appConfig: AppConfigService
    ) {}

    /**
     * Effective organization name: the org selected by a super-admin takes precedence,
     * otherwise the logged-in user's own organization. Returned lowercased/trimmed.
     */
    private getEffectiveOrganizationName(): string | null {
        const selected = this._appConfig.getSelectedOrganizationName();
        if (selected && selected.trim()) {
            return selected.trim().toLowerCase();
        }
        return this._organization.getOrganizationName();
    }

    /** True when the effective organization is APPCELERATE LATAM. */
    isLatam(): boolean {
        const name = this.getEffectiveOrganizationName();
        if (!name) {
            return false;
        }
        return LATAM_ORGANIZATION_NAMES.map((n) => n.trim().toLowerCase()).includes(name);
    }

    /** Minimum CPM (€) for the current organization. */
    getCpmMinimum(): number {
        return this.isLatam() ? LATAM_CPM_MINIMUM : DEFAULT_CPM_MINIMUM;
    }

    /** Minimum CPC (€) for the current organization. */
    getCpcMinimum(): number {
        return DEFAULT_CPC_MINIMUM;
    }

    /**
     * Apply the organization bid minimums to a line-item pricing form (a group with
     * `pricing_type`, `pricing_value` and an optional `max_cpm`): pricing_value uses the CPM
     * minimum for CPM, the CPC minimum for CPC and >= 0 otherwise, while max_cpm (a CPM cap)
     * shares the CPM minimum. Call it whenever `pricing_type` changes.
     */
    applyPricingValidators(form: FormGroup): void {
        const cpmMin = this.getCpmMinimum();
        const pricingType = form.get("pricing_type")?.value;
        const valueMin = pricingType === "cpm" ? cpmMin : pricingType === "cpc" ? this.getCpcMinimum() : 0;

        const pricingValue = form.get("pricing_value");
        pricingValue?.setValidators([Validators.required, Validators.min(valueMin)]);
        pricingValue?.updateValueAndValidity({ emitEvent: false });

        const maxCpm = form.get("max_cpm");
        maxCpm?.setValidators([Validators.min(cpmMin)]);
        maxCpm?.updateValueAndValidity({ emitEvent: false });
    }
}
